import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { GpmVisibility } from "@design-system";
import { Spin } from "antd";
import { UPDATE_SETTINGS } from "../../../graphql/mutation";
import { useMutation, useSettings } from "../../../hooks";
import { FIND_LATEST_SETTINGS_QUERY, FEE_EARNER_LEVELS_QUERY } from "../../../graphql/query";
import { UPDATE_FEE_EARNER_LEVELS } from "../../../graphql/mutation/update-fee-earner-levels";

type UpdatePayload = {
  id: number;
  restrictedGpmVisible: boolean;
};

export const GpmVisibilityContainer = () => {
  const { loading, settings } = useSettings();
  const { data: feeEarnerLevels } = useQuery(FEE_EARNER_LEVELS_QUERY);

  const [updatePayloads, setUpdatePayloads] = useState<Record<string, UpdatePayload>>({});

  const [updateFeeEarnerLevelsMutation] = useMutation(UPDATE_FEE_EARNER_LEVELS);

  const gpmWarningThreshold = settings?.gpmWarningThreshold;

  const updateFeeEarnerLevels = (data: UpdatePayload[]) => {
    data.forEach((payload) => {
      updatePayloads[payload.id] = payload;
    });
    setUpdatePayloads({ ...updatePayloads });
  };

  const [updateSettings] = useMutation(UPDATE_SETTINGS, {
    update(cache, { data }) {
      const updateSettings = data?.updateSettings;
      if (updateSettings) {
        cache.writeQuery({
          query: FIND_LATEST_SETTINGS_QUERY,
          data: {
            findLatestSettings: updateSettings,
          },
        });
      }
    },
  });

  const onSubmit = () => {
    const payloadArray = Object.values(updatePayloads);
    updateFeeEarnerLevelsMutation({
      variables: {
        input: payloadArray.map((x) => ({
          id: x.id,
          payload: { restrictedGpmVisible: { set: x.restrictedGpmVisible } },
        })),
      },
      update: (cache, { data }) => {
        const feeEarnerLevels = data?.updateFeeEarnerLevels;
        if (feeEarnerLevels) {
          feeEarnerLevels.forEach((updatedItem) => {
            cache.modify({
              id: cache.identify({
                __typename: "FeeEarnerLevel",
                id: updatedItem.id,
              }),
              fields: {
                restrictedGpmVisible() {
                  return updatedItem.restrictedGpmVisible;
                },
              },
            });
          });
        }
      },
    });
  };

  return (
    <>
      {loading ? (
        <Spin size="large" />
      ) : (
        <GpmVisibility
          key={settings.id}
          settings={settings}
          feeEarnerLevels={feeEarnerLevels?.feeEarnerLevels.records}
          onUpdateSettings={async (settingsData) => {
            await updateSettings({
              variables: {
                id: settings.id,
                data: {
                  restrictGpmVisibility: { set: settingsData.restrictGpmVisibility },
                  showGpmSlider: { set: settingsData.showGpmSlider ?? false },
                  ...(gpmWarningThreshold == null && settingsData.showGpmSlider
                    ? { gpmWarningThreshold: { set: 10 } }
                    : {}),
                },
              },
              context: {
                hasUpload: true,
              },
              update: (cache, { data }) => {
                const updateSettings = data?.updateSettings;
                if (updateSettings) {
                  cache.writeQuery({
                    query: FIND_LATEST_SETTINGS_QUERY,
                    data: {
                      findLatestSettings: updateSettings,
                    },
                  });
                }
              },
            });
          }}
          onUpdateFeeEarnerLevels={updateFeeEarnerLevels}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};
